import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import Keycloak from 'keycloak-js';
import { environment } from 'src/environments/environment';
import { HelperService } from '../shared/services/helper.service';
import { APIRequestService } from 'src/app/shared/services/api/apirequest.service';
import { APIURLTypes } from 'src/app/shared/enums/apiurltypes.enum';

@Injectable({
  providedIn: 'root',
})
export class KeycloakService {
  private _keycloak: Keycloak.KeycloakInstance;

  constructor(private router: Router, private helperService: HelperService) {}

  init(): Promise<void> {
    return new Promise((resolve) => {
      console.log('🔧 [KeycloakService] Starting Keycloak initialization...');
      console.log('🔧 [KeycloakService] Current URL:', window.location.href);
      console.log('🔧 [KeycloakService] Current pathname:', window.location.pathname);
      console.log('🔧 [KeycloakService] Current search:', window.location.search);

      const searchParams = new URLSearchParams(window.location.search);
      const hasIframeAccessToken = searchParams.has('accessToken');
      if (hasIframeAccessToken) {
        this.helperService.embeddedMode.set(true);
        this.helperService.iframeBootstrap.set(true);
        console.log('🔧 [KeycloakService] Iframe bootstrap token detected. Skipping Keycloak init.');
        resolve();
        return;
      }
      
      // Save original URL FIRST, before any authentication logic
      const currentUrl = window.location.pathname + window.location.search;
      const hashParams = new URLSearchParams(window.location.hash.substring(1));
      const isCallback = hashParams.has('state') && hashParams.has('code');
      
      console.log('🔧 [KeycloakService] Current URL to potentially save:', currentUrl);
      console.log('🔧 [KeycloakService] Hash params:', window.location.hash);
      console.log('🔧 [KeycloakService] Is callback?', isCallback);
      
      if (!isCallback && 
          currentUrl && 
          currentUrl !== '/' && 
          !currentUrl.includes('/auth/') && 
          !currentUrl.includes('/kc/')) {
        console.log('🔧 [KeycloakService] ✅ SAVING original URL at initialization:', currentUrl);
        this.helperService.originalUrl.set(currentUrl);
        
        // Verify it was saved
        const savedUrl = this.helperService.originalUrl.get();
        console.log('🔧 [KeycloakService] ✅ VERIFIED saved URL:', savedUrl);
      } else {
        console.log('🔧 [KeycloakService] ❌ NOT saving URL - reason: callback URL or excluded path');
      }
      
      this._keycloak = new Keycloak({
        url: environment.keycloak.url,
        realm: environment.keycloak.realm,
        clientId: environment.keycloak.clientId,
      });

      if (isCallback) {
        console.log('🔧 [KeycloakService] Handling redirect from Keycloak...');
        this._keycloak.init({ onLoad: 'check-sso' })
          .then((authenticated) => {
            if (authenticated) {
              console.log('🔧 [KeycloakService] Callback successful. User is authenticated.');
              this.router.navigate(['/kc/callback']);
              resolve();
            } else {
              console.error('🔧 [KeycloakService] Callback failed. Authentication is false.');
              this.router.navigate(['/auth/signin']);
              resolve();
            }
          }).catch(() => {
            console.error('🔧 [KeycloakService] Critical error during callback handling.');
            resolve();
          });
      } else {
        console.log('🔧 [KeycloakService] Starting SSO check...');
        this._keycloak.init({ onLoad: 'check-sso' })
          .then((authenticated) => {
            console.log('🔧 [KeycloakService] SSO check result - authenticated:', authenticated);
            if (authenticated) {
              console.log('🔧 [KeycloakService] SSO session exists. Redirecting to Keycloak for login.');
              console.log('🔧 [KeycloakService] Initiating Keycloak login...');
              this._keycloak.login();
            } else {
              // Check for direct login flag
              // @ts-ignore
              if (environment.enableKeycloakDirectLogin) {
                console.log('🔧 [KeycloakService] Direct login enabled. Initiating Keycloak login despite no SSO cookie.');
                this._keycloak.login();
              } else {
                console.log('🔧 [KeycloakService] No SSO session cookie. Proceeding with normal flow.');
                resolve();
              }
            }
          }).catch((error) => {
            console.error('🔧 [KeycloakService] Critical error during SSO check:', error);
            resolve();
          });
      }
    });
  }

  isLoggedIn(): boolean {
    return this._keycloak?.authenticated || false;
  }

  getToken(): string | undefined {
    return this._keycloak?.token;
  }

  getUserProfile(): Promise<any> {
    return this._keycloak?.loadUserProfile();
  }
  
  getRoles(): string[] {
    return this._keycloak?.realmAccess?.roles || [];
  }

  logout(): void {
    console.log('KeycloakService: Logging out and clearing session...');
    this.helperService.clearAllData();
    this._keycloak?.logout({ redirectUri: environment.keycloak.logoutRedirectUri });
  }

  updateToken(): Promise<string> {
    return new Promise((resolve, reject) => {
      if (!this._keycloak) {
        if (this.helperService.embeddedMode.get()) {
          const storedToken = this.helperService.token.get();
          if (storedToken) {
            return resolve(storedToken);
          }
          return reject('IFRAME_AUTH_EXPIRED');
        }
        return reject('Keycloak instance not initialized');
      }

      this._keycloak.updateToken(5)
        .then((refreshed) => {
          if (refreshed) {
            console.log('Keycloak token refreshed');
            const token = this._keycloak.token;
            this.helperService.token.set(token);
            resolve(token);
          } else {
            console.log('Keycloak token still valid');
            resolve(this._keycloak.token);
          }
        })
        .catch(() => {
          console.error('Keycloak token refresh failed. Forcing logout.');
          this.logout();
          reject('Failed to refresh token');
        });
    });
  }
} 